// RightDrawer.tsx
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface RightDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full' | '50' | '60';
  footer?: React.ReactNode;
}

export default function RightDrawer({
  open,
  onClose,
  title,
  children,
  size = '60',
  footer
}: RightDrawerProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Close drawer on Escape key press
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    if (open) window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop Overlay - high z-index so drawer shows above all (headers, dropdowns, etc.) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99998]"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: "easeInOut", duration: 0.3 }}
            className={`fixed inset-y-0 right-0 z-[99999] w-full bg-white shadow-2xl flex flex-col ${size === 'full' ? 'max-w-full' :
              size === '3xl' ? 'max-w-3xl' :
                size === '2xl' ? 'max-w-2xl' :
                  size === 'xl' ? 'max-w-xl' :
                    size === 'lg' ? 'max-w-lg' :
                      size === '60' ? 'max-w-[60%]' :
                        size === '50' ? 'max-w-[50%]' :
                          'max-w-md'
              }`}
          >
            {/* Header - Only show when title is provided */}
            {title && (
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
                <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                  aria-label="Close drawer"
                >
                  <X size={20} />
                </button>
              </div>
            )}

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              <div className="p-6">
                {children}
              </div>
            </div>
            {/* Footer */}
            {footer && (
              <div className="flex-shrink-0 border-t border-gray-200 bg-white">
                {footer}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}